import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ROUTES } from '@/common';
import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useBreadcrumbs, useCurrentOrganization, useRedirectHomeAccess } from '@/modules/core/hooks';
import useGetOrganizationById from '@/modules/core/hooks/useGetOrganizationById';
import useGetOrgName from '@/modules/employer/queries/useGetOrgName.query';
import { useIsApplyUnmatchedPushAvailable } from '@/modules/featureFlags';

import { FundsHistory } from './FundsHistory/components';
import { useFundsHistoryFilters } from './FundsHistory/hooks';
import { ApplyFundsBar, ApplyFundsModals, UnpaidInvoices } from './UnpaidInvoices/components';
import {
  useApplyFundsFlow,
  useUnpaidInvoicesFilters,
  useUnpaidInvoicesSelection,
} from './UnpaidInvoices/hooks';
import { UnappliedFunds } from './components';
import { useOrganizationAncestors, useOrganizationPath, useUnappliedFundsTab } from './hooks';
import {
  useApplyUnmatchedInvoicesQuery,
  useFundsHistoryQuery,
  useUnappliedFundsBalanceQuery,
  useUnpaidInvoicesQuery,
} from './queries';
import { UNPAID_INVOICE_TYPE_OPTIONS } from './UnappliedFunds.constants';
import { UnappliedFundsTab } from './UnappliedFunds.types';

const ACCESS_REQUIREMENTS = [{ permission: Permissions.VIEW_OMNI_BALANCES }];

const UnappliedFundsContainer: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentTab, changeTab } = useUnappliedFundsTab();
  const isAvailable = useIsApplyUnmatchedPushAvailable();
  const organizationId = isAvailable ? orgId : undefined;

  useRedirectHomeAccess(ACCESS_REQUIREMENTS);

  const { defaultOrganizationData, isObservingModeActive } = useCurrentOrganization();
  const isSystemLevel = defaultOrganizationData.type === OrganizationTypes.system && !isObservingModeActive;

  const { formattedData: organization } = useGetOrganizationById(organizationId);
  const { balance, isLoading: isBalanceLoading } = useUnappliedFundsBalanceQuery(organizationId);
  const {
    page,
    setPage,
    perPage,
    setPerPage,
    searchString,
    setSearchString,
    currentSearch,
    types,
    setTypes,
    invoiceTypes,
    hasActiveFilters,
  } = useUnpaidInvoicesFilters();
  const {
    invoices,
    total: unpaidInvoicesTotal,
    isLoading: isInvoicesLoading,
  } = useUnpaidInvoicesQuery({
    organizationId,
    searchInput: currentSearch,
    invoiceTypes,
    page: page - 1,
    size: perPage,
  });
  const {
    page: fundsPage,
    setPage: setFundsPage,
    perPage: fundsPerPage,
    setPerPage: setFundsPerPage,
    searchString: fundsSearchString,
    setSearchString: setFundsSearchString,
    currentSearch: fundsCurrentSearch,
  } = useFundsHistoryFilters();
  const {
    transactions,
    total: fundsHistoryTotal,
    isLoading: isFundsHistoryLoading,
  } = useFundsHistoryQuery({
    organizationId,
    search: fundsCurrentSearch,
    page: fundsPage - 1,
    size: fundsPerPage,
  });
  const {
    selectedIds,
    selectedAmount,
    balanceAfterApply,
    disabledIds,
    isAllSelected,
    isSelectAllDisabled,
    toggleInvoice,
    toggleAll,
    clearSelection,
  } = useUnpaidInvoicesSelection({ invoices, balance });
  const { handleApply, isLoading: isApplying } = useApplyUnmatchedInvoicesQuery();
  const {
    isConfirmVisible,
    isSuccessVisible,
    isErrorVisible,
    openConfirm,
    closeConfirm,
    closeSuccess,
    closeError,
    confirm,
  } = useApplyFundsFlow({ selectedIds, applyFunds: handleApply, onApplied: clearSelection });

  const ancestorsWithoutNames = useOrganizationAncestors({
    organizationId,
    organizationPath: isSystemLevel ? organization.path : undefined,
  });
  const { name: firstAncestorName } = useGetOrgName({ id: ancestorsWithoutNames[0]?.id, enabled: true });
  const { name: secondAncestorName } = useGetOrgName({ id: ancestorsWithoutNames[1]?.id, enabled: true });
  const { name: thirdAncestorName } = useGetOrgName({ id: ancestorsWithoutNames[2]?.id, enabled: true });

  const ancestors = useMemo(
    () =>
      ancestorsWithoutNames.map((ancestor, index) => ({
        ...ancestor,
        name: [firstAncestorName, secondAncestorName, thirdAncestorName][index],
      })),
    [ancestorsWithoutNames, firstAncestorName, secondAncestorName, thirdAncestorName]
  );
  const { pathItems, handleSelectOrganization } = useOrganizationPath({ ancestors });

  const tabs = useMemo(
    () => [
      {
        key: UnappliedFundsTab.UNPAID_INVOICES,
        title: `Unpaid invoices (${unpaidInvoicesTotal})`,
      },
      {
        key: UnappliedFundsTab.FUNDS_HISTORY,
        title: `Funds history (${fundsHistoryTotal})`,
      },
    ],
    [unpaidInvoicesTotal, fundsHistoryTotal]
  );

  useBreadcrumbs(
    [
      {
        route: ROUTES.UNAPPLIED_FUNDS,
        title: 'Unapplied Funds',
      },
      {
        route: ROUTES.UNAPPLIED_FUNDS_ORG_DETAILS(orgId),
        title: organization.name ? `${organization.name} – Unapplied Funds` : '',
      },
    ],
    [orgId, organization.name],
    { backBtn: true }
  );

  if (!isAvailable) {
    return null;
  }

  return (
    <UnappliedFunds
      organizationName={organization.name}
      balance={balance}
      isBalanceLoading={isBalanceLoading}
      pathItems={pathItems}
      onSelectPathOrganization={handleSelectOrganization}
      tabs={tabs}
      currentTab={currentTab}
      onChangeTab={changeTab}
    >
      {currentTab === UnappliedFundsTab.UNPAID_INVOICES && (
        <>
          <UnpaidInvoices
            invoices={invoices}
            total={unpaidInvoicesTotal}
            isLoading={isInvoicesLoading}
            hasActiveFilters={hasActiveFilters}
            page={page}
            perPage={perPage}
            onChangePage={setPage}
            onChangePerPage={setPerPage}
            searchString={searchString}
            onChangeSearch={setSearchString}
            typeOptions={UNPAID_INVOICE_TYPE_OPTIONS}
            types={types}
            onChangeTypes={setTypes}
            selectedIds={selectedIds}
            disabledIds={disabledIds}
            isAllSelected={isAllSelected}
            isSelectAllDisabled={isSelectAllDisabled}
            onToggleInvoice={toggleInvoice}
            onToggleAll={toggleAll}
          />
          <ApplyFundsBar
            selectedCount={selectedIds.length}
            selectedAmount={selectedAmount}
            balanceAfterApply={balanceAfterApply}
            isApplying={isApplying}
            onClear={clearSelection}
            onApply={openConfirm}
          />
        </>
      )}
      {currentTab === UnappliedFundsTab.FUNDS_HISTORY && (
        <FundsHistory
          transactions={transactions}
          total={fundsHistoryTotal}
          isLoading={isFundsHistoryLoading}
          hasActiveSearch={Boolean(fundsCurrentSearch)}
          page={fundsPage}
          perPage={fundsPerPage}
          onChangePage={setFundsPage}
          onChangePerPage={setFundsPerPage}
          searchString={fundsSearchString}
          onChangeSearch={setFundsSearchString}
        />
      )}
      <ApplyFundsModals
        selectedCount={selectedIds.length}
        selectedAmount={selectedAmount}
        isConfirmVisible={isConfirmVisible}
        isSuccessVisible={isSuccessVisible}
        isErrorVisible={isErrorVisible}
        onConfirm={confirm}
        onCloseConfirm={closeConfirm}
        onCloseSuccess={closeSuccess}
        onCloseError={closeError}
      />
    </UnappliedFunds>
  );
};

export default UnappliedFundsContainer;
